import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Popconfirm,
  Tag,
  message,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import { adminApi } from '../../api/apiService';

const { Title } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // Modal states
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Form refs
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // Load users on component mount and pagination change
  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getUsersWithStats(
        pagination.current,
        pagination.pageSize
      );
      
      setUsers(response.users || []);
      setPagination({
        ...pagination,
        total: response.total || 0
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize
    });
  };

  // Create user
  const showCreateModal = () => {
    createForm.resetFields();
    setIsCreateModalVisible(true);
  };

  const handleCreateUser = async (values) => {
    try {
      await adminApi.createUser(values);
      message.success('User created successfully');
      setIsCreateModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      message.error('Failed to create user');
    }
  };

  // Edit user
  const showEditModal = async (user) => {
    setCurrentUser(user);
    editForm.setFieldsValue({
      role: user.role,
      rate: user.rate,
      status: user.status || 'active',
      balance: 0 // This is an increment, not the current value
    });
    
    try {
      // Get detailed stats for the user if not already available
      if (!user.account_count || !user.subscribed_numbers_count) {
        const userStats = await adminApi.getUserStats(user.uid);
        setCurrentUser(prev => ({
          ...prev,
          account_count: userStats.account_count,
          subscribed_numbers_count: userStats.subscribed_numbers_count
        }));
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
    
    setIsEditModalVisible(true);
  };

  const handleEditUser = async (values) => {
    try {
      await adminApi.updateUser(currentUser.uid, values);
      message.success('User updated successfully');
      setIsEditModalVisible(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Failed to update user');
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    try {
      await adminApi.deleteUser(userId);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Failed to delete user');
    }
  };

  // Update user status
  const toggleUserStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await adminApi.toggleUserStatus(userId, newStatus);
      message.success(`User ${newStatus === 'active' ? 'activated' : 'disabled'} successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      message.error('Failed to update user status');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Username',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email ? email.split('@')[0] : 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status || 'active'}
        </Tag>
      ),
    },
    {
      title: 'Accounts',
      dataIndex: 'account_count',
      key: 'account_count',
      render: (count) => count || 0,
    },
    {
      title: 'Subscribed Numbers',
      dataIndex: 'subscribed_numbers_count',
      key: 'subscribed_numbers_count',
      render: (count) => count || 0,
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: (balance) => `$${balance?.toFixed(2) || '0.00'}`,
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate) => `$${rate?.toFixed(2) || '0.00'}`,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record)}
            size="small"
          />
          <Button
            icon={record.status === 'active' ? <LockOutlined /> : <UnlockOutlined />}
            onClick={() => toggleUserStatus(record.uid, record.status || 'active')}
            size="small"
            type={record.status === 'active' ? 'default' : 'primary'}
          />
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.uid)}
            okText="Yes"
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              danger 
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>User Management</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={showCreateModal}
        >
          Create User
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="uid"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Total ${total} users`
        }}
        loading={loading}
        onChange={handleTableChange}
      />

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        visible={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please input the username!' }]}
          >
            <Input placeholder="Username" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input the password!' }]}
          >
            <Input.Password placeholder="Password" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            initialValue="user"
          >
            <Select>
              <Option value="user">User</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="rate"
            label="Rate ($)"
            initialValue={1.0}
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="active"
          >
            <Select>
              <Option value="active">Active</Option>
              <Option value="disabled">Disabled</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Create User
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        visible={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        footer={null}
      >
        {currentUser && (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEditUser}
          >
            <Form.Item label="Username">
              <Input value={currentUser.email ? currentUser.email.split('@')[0] : ''} disabled />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <Typography.Text strong>Accounts: </Typography.Text>
              <Typography.Text>{currentUser.account_count || 0}</Typography.Text>
              <br />
              <Typography.Text strong>Subscribed Numbers: </Typography.Text>
              <Typography.Text>{currentUser.subscribed_numbers_count || 0}</Typography.Text>
            </div>

            <Form.Item
              name="role"
              label="Role"
            >
              <Select>
                <Option value="user">User</Option>
                <Option value="admin">Admin</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="rate"
              label="Rate ($)"
            >
              <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
            >
              <Select>
                <Option value="active">Active</Option>
                <Option value="disabled">Disabled</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="balance"
              label="Add to Balance ($)"
              extra="Enter amount to add to current balance"
            >
              <InputNumber min={-1000} step={0.01} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Update User
              </Button>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement; 